// favorite (add, remove)
import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';
import axios from 'axios';
import utils from './utils';

import { getCounts } from './info';

let lastGetItemSignature = Symbol();

const status = utils.initStatus([
  'getItems',
  'getPendingItems',
  'deleteItem',
  'addItem',
  'getItemDetails',
  'updateItem',
  'addItemCategory',
  'createAddon',
  'getItemCategory',
  'bulkDelete',
  'bulkHide',
]);

const initFilter = () => {
  return {
    keyword: '',
    page: 1,
    skip: 200,
  };
};

const initialState = {
  items: [],
  displayShowMore: true,
  pendingItems: [],
  itemsView: 'grid',
  pendingView: 'grid',

  cachedItems: [
    {
      page: 1,
      items: [],
    },
  ],

  itemDetails: {
    name: '',
    description: '',
    price: 0,
    addOns: [],
    category: '',
    categoryIcon: '',
    restrictions: [],
    qty: 1,
    photos: [],
  },

  addOns: [],

  filters: {
    items: initFilter(),
  },
  status,
};
export const getItems = createAsyncThunk('item/getItems', async (payload, thunkAPI) => {
  const signature = Symbol();

  lastGetItemSignature = signature;

  try {
    const { currBranch, token } = utils.getAuth(thunkAPI.getState());

    if (!token)
      return utils.rejectWithValue(thunkAPI, null, {
        logout: true,
        message: 'Null token',
      });

    const itemsFilter = thunkAPI.getState().item.filters.items;

    let res;
    if (itemsFilter.noHidden)
      res = await axios.get(`/merchant/list/items/${currBranch}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { ...itemsFilter, status: 'active,approved' },
      });
    else
      res = await axios.get(`/merchant/list/items/${currBranch}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { ...itemsFilter, status: 'active,approved,hidden' },
      });

    if (res.status == 200) {
      return { ...res.data, signature };
    }

    return utils.rejectWithValue(thunkAPI, null, {
      message: res.data.message,
      status: res.status,
    });
  } catch (err) {
    return utils.rejectWithValue(thunkAPI, err);
  }
});

export const getPendingItems = createAsyncThunk(
  'item/getPendingItems',
  async (params, thunkAPI) => {
    try {
      const { currBranch, token } = utils.getAuth(thunkAPI.getState());
      if (!token)
        return utils.rejectWithValue(thunkAPI, null, {
          logout: true,
          message: 'Null token',
        });

      const itemsFilter = thunkAPI.getState().item.filters.items;
      const res = await axios.get(`/merchant/list/items/${currBranch}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { ...itemsFilter, status: 'pending' },
      });
      if (res.status == 200) {
        return res.data;
      }
      return utils.rejectWithValue(thunkAPI, null, {
        message: res.data.message,
        status: res.status,
      });
    } catch (err) {
      return utils.rejectWithValue(thunkAPI, err);
    }
  },
);

export const addItem = createAsyncThunk('item/addItem', async (item, thunkAPI) => {
  try {
    const { currBranch, token } = utils.getAuth(thunkAPI.getState());
    if (!token)
      return utils.rejectWithValue(thunkAPI, null, {
        logout: true,
        message: 'Null token',
      });

    const res = await axios.post(`/merchant/add/item/${currBranch}`, item, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (res.status == 200) {
      thunkAPI.dispatch(getItems());

      return res.data;
    }
    return utils.rejectWithValue(thunkAPI, null, {
      message: res.data.message,
      status: res.status,
    });
  } catch (err) {
    return utils.rejectWithValue(thunkAPI, err);
  }
});

export const deleteItem = createAsyncThunk('item/deleteItem', async (itemId, thunkAPI) => {
  try {
    const { currBranch, token } = utils.getAuth(thunkAPI.getState());
    if (!token)
      return utils.rejectWithValue(thunkAPI, null, {
        logout: true,
        message: 'Null token',
      });

    const res = await axios.delete(`/merchant/delete/item/${currBranch}`, {
      headers: { Authorization: `Bearer ${token}` },
      data: { itemId },
    });

    if (res.status == 200) {
      thunkAPI.dispatch(getItems());
      thunkAPI.dispatch(getCounts());
      return res.data;
    }
    return utils.rejectWithValue(thunkAPI, null, {
      message: res.data.message,
      status: res.status,
    });
  } catch (err) {
    return utils.rejectWithValue(thunkAPI, err);
  }
});

export const updateItem = createAsyncThunk('item/updateItem', async (item, thunkAPI) => {
  try {
    const { currBranch, token } = utils.getAuth(thunkAPI.getState());
    if (!token)
      return utils.rejectWithValue(thunkAPI, null, {
        logout: true,
        message: 'Null token',
      });

    const res = await axios.post(`/merchant/update/res/item/${currBranch}`, item, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (res.status == 200) {
      thunkAPI.dispatch(getItems());
      thunkAPI.dispatch(clearState());

      return res.data;
    }
    return utils.rejectWithValue(thunkAPI, null, {
      message: res.data.message,
      status: res.status,
    });
  } catch (err) {
    return utils.rejectWithValue(thunkAPI, err);
  }
});

export const getItemCategory = createAsyncThunk('item/getItemCategory', async (item, thunkAPI) => {
  try {
    const { currBranch, token } = utils.getAuth(thunkAPI.getState());
    if (!token)
      return utils.rejectWithValue(thunkAPI, null, {
        logout: true,
        message: 'Null token',
      });

    const res = await axios.get(`/merchant/list/item-categories/${currBranch}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status == 200) {
      thunkAPI.dispatch(getItems());

      return res.data;
    }
    return utils.rejectWithValue(thunkAPI, null, {
      message: res.data.message,
      status: res.status,
    });
  } catch (err) {
    return utils.rejectWithValue(thunkAPI, err);
  }
});

export const getItemDetails = createAsyncThunk('item/getItemDetails', async (itemId, thunkAPI) => {
  try {
    const { currBranch, token } = utils.getAuth(thunkAPI.getState());
    if (!token)
      return utils.rejectWithValue(thunkAPI, null, {
        logout: true,
        message: 'Null token',
      });

    const res = await axios.get(`/merchant/item/details/${currBranch}`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { itemId },
    });

    if (res.status == 200) {
      thunkAPI.dispatch(getItems());

      return res.data;
    }
    return utils.rejectWithValue(thunkAPI, null, {
      message: res.data.message,
      status: res.status,
    });
  } catch (err) {
    return utils.rejectWithValue(thunkAPI, err);
  }
});

export const addItemCategory = createAsyncThunk(
  'item/addItemCategory',
  async (category, thunkAPI) => {
    try {
      const { currBranch, token } = utils.getAuth(thunkAPI.getState());
      if (!token)
        return utils.rejectWithValue(thunkAPI, null, {
          logout: true,
          message: 'Null token',
        });

      const res = await axios.post(`/merchant/add/category`, category, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status == 200) {
        return res.data;
      }
      return utils.rejectWithValue(thunkAPI, null, {
        message: res.data.message,
        status: res.status,
      });
    } catch (err) {
      return utils.rejectWithValue(thunkAPI, err);
    }
  },
);

export const createAddon = createAsyncThunk('item/createAddon', async (addOn, thunkAPI) => {
  try {
    const { currBranch, token } = utils.getAuth(thunkAPI.getState());
    if (!token)
      return utils.rejectWithValue(thunkAPI, null, {
        logout: true,
        message: 'Null token',
      });

    const res = await axios.post(`/merchant/add/add-on/${currBranch}`, addOn, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (res.status == 200) {
      return res.data;
    }
    return utils.rejectWithValue(thunkAPI, null, {
      message: res.data.message,
      status: res.status,
    });
  } catch (err) {
    return utils.rejectWithValue(thunkAPI, err);
  }
});

export const bulkDelete = createAsyncThunk('item/bulkDelete', async (items, thunkAPI) => {
  try {
    const { currBranch, token } = utils.getAuth(thunkAPI.getState());
    if (!token)
      return utils.rejectWithValue(thunkAPI, null, {
        logout: true,
        message: 'Null token',
      });
    const res = await axios.delete(`/merchant/delete/bulk/items/${currBranch}`, {
      headers: { Authorization: `Bearer ${token}` },
      data: { items: JSON.stringify(items) },
    });

    if (res.status == 200) {
      thunkAPI.dispatch(getItems());
      thunkAPI.dispatch(getCounts());
      return res.data;
    }
    return utils.rejectWithValue(thunkAPI, null, {
      message: res.data.message,
      status: res.status,
    });
  } catch (err) {
    return utils.rejectWithValue(thunkAPI, err);
  }
});

export const bulkHide = createAsyncThunk('item/bulkHide', async ({ type, items }, thunkAPI) => {
  try {
    const { currBranch, token } = utils.getAuth(thunkAPI.getState());
    if (!token)
      return utils.rejectWithValue(thunkAPI, null, {
        logout: true,
        message: 'Null token',
      });

    const res = await axios.put(
      `/merchant/hide-unhide/items/${currBranch}`,
      { type, items: JSON.stringify(items) },
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );

    if (res.status == 200) {
      thunkAPI.dispatch(getItems());
      return res.data;
    }
    return utils.rejectWithValue(thunkAPI, null, {
      message: res.data.message,
      status: res.status,
    });
  } catch (err) {
    return utils.rejectWithValue(thunkAPI, err);
  }
});

export const slice = createSlice({
  name: 'item',
  initialState,
  reducers: {
    clearState: (state, { payload }) => {
      if (['updateItem', 'addItem'].includes(payload?.name)) {
        state.itemDetails = initialState.itemDetails;
      }
      utils.clearState(state, status, payload);
    },
    updateFilter: (state, { payload }) => {
      const key = Object.keys(payload)[0];
      state.filters = {
        ...state.filters,
        [key]: { ...state.filters[key], ...payload[key] },
      };
      state.displayShowMore = true;
      return state;
    },

    addAddon: (state, { payload }) => {
      state.itemDetails.addOns = [
        ...state.itemDetails.addOns,
        {
          name: '',
          required: false,
          limit: '1',
          choices: [
            {
              name: '',
              price: '0',
            },
          ],
        },
      ];

      return state;
    },
    removeAddon: (state, { payload }) => {
      state.itemDetails.addOns = state.itemDetails.addOns.filter((val, i) => i !== payload);
      return state;
    },

    updateAddon: (state, { payload }) => {
      const { index, field, value } = payload;
      state.itemDetails.addOns[index][field] = value;

      return state;
    },

    updateAddonChoice: (state, { payload }) => {
      const { index, choiceIndex, value, field } = payload;
      state.itemDetails.addOns[index].choices[choiceIndex][field] = value;

      return state;
    },
    removeAddonChoice: (state, { payload }) => {
      const { index, choiceIndex } = payload;
      state.itemDetails.addOns[index].choices = state.itemDetails.addOns[index].choices.filter(
        (val, i) => i !== choiceIndex,
      );
      return state;
    },
    setView: (state, { payload }) => {
      if (payload.items) {
        state.itemsView = payload.items;
      }
      if (payload.pending) {
        state.pendingView = payload.pending;
      }
    },
  },
  extraReducers: {
    /*
        Get Items
        */
    [getItems.fulfilled]: (state, { payload }) => {
      const { data, message, signature } = payload;

      if (data.length < 1) {
        state.displayShowMore = false;
      }

      if (signature === lastGetItemSignature) {
        if (current(state.filters.items).page > 1) {
          let allItems = current(state.items);
          allItems = allItems.concat(data);
          state.items = allItems;
        } else {
          state.items = data;
        }

        utils.handleSuccess(state.status.getItems, {
          message: message,
          status: 200,
        });
      }

      return state;
    },
    [getItems.rejected]: (state, { payload }) =>
      utils.handleError(state.status.getItems, payload?.data),
    [getItems.pending]: (state) => utils.handleLoading(state.status.getItems),
    /*
        Get Pending tems
        */
    [getPendingItems.fulfilled]: (state, { payload }) => {
      const { data, message } = payload;

      state.pendingItems = data;

      utils.handleSuccess(state.status.getPendingItems, {
        message: message,
        status: 200,
      });

      return state;
    },
    [getPendingItems.rejected]: (state, { payload }) =>
      utils.handleError(state.status.getPendingItems, payload?.data),
    [getPendingItems.pending]: (state) => utils.handleLoading(state.status.getPendingItems),

    /*
        Get Charity Choice
        */
    [deleteItem.fulfilled]: (state, { payload }) => {
      const { data, message } = payload;
      state.choice = data;

      utils.handleSuccess(state.status.deleteItem, {
        message: message,
        status: 200,
      });

      return state;
    },
    [deleteItem.rejected]: (state, { payload }) =>
      utils.handleError(state.status.deleteItem, payload?.data),
    [deleteItem.pending]: (state) => utils.handleLoading(state.status.deleteItem),

    /*
        Set Charity Choice
        */
    [addItem.fulfilled]: (state, { payload }) => {
      const { data, message, permission } = payload;
      state.status.addItem.permission = permission;
      utils.handleSuccess(state.status.addItem, {
        message: message,
        status: 200,
      });

      return state;
    },
    [addItem.rejected]: (state, { payload }) =>
      utils.handleError(state.status.addItem, payload?.data),
    [addItem.pending]: (state) => utils.handleLoading(state.status.addItem),

    /*
        Get Item Details
        */
    [getItemDetails.fulfilled]: (state, { payload }) => {
      const { data, message } = payload;
      state.itemDetails = data;
      state.addOns = [];

      utils.handleSuccess(state.status.getItemDetails, {
        message: message,
        status: 200,
      });

      return state;
    },
    [getItemDetails.rejected]: (state, { payload }) =>
      utils.handleError(state.status.getItemDetails, payload?.data),
    [getItemDetails.pending]: (state) => utils.handleLoading(state.status.getItemDetails),

    /*
        Update Item
        */
    [updateItem.fulfilled]: (state, { payload }) => {
      const { data, message, permission } = payload;
      state.status.updateItem.permission = permission;

      utils.handleSuccess(state.status.updateItem, {
        message: message,
        status: 200,
      });

      return state;
    },
    [updateItem.rejected]: (state, { payload }) =>
      utils.handleError(state.status.updateItem, payload?.data),
    [updateItem.pending]: (state) => utils.handleLoading(state.status.updateItem),

    /*
        Add Item Category
        */
    [addItemCategory.fulfilled]: (state, { payload }) => {
      const { data, message } = payload;

      utils.handleSuccess(state.status.addItemCategory, {
        message: message,
        status: 200,
      });

      return state;
    },
    [addItemCategory.rejected]: (state, { payload }) =>
      utils.handleError(state.status.addItemCategory, payload?.data),
    [addItemCategory.pending]: (state) => utils.handleLoading(state.status.addItemCategory),

    /*
		Create Addon
        */
    [createAddon.fulfilled]: (state, { payload }) => {
      const { data, message } = payload;
      state.addOns = [...state.addOns, data];
      utils.handleSuccess(state.status.createAddon, {
        message: message,
        status: 200,
      });

      return state;
    },
    [createAddon.rejected]: (state, { payload }) =>
      utils.handleError(state.status.createAddon, payload?.data),
    [createAddon.pending]: (state) => utils.handleLoading(state.status.createAddon),

    /*
		Item Category
        */
    [getItemCategory.fulfilled]: (state, { payload }) => {
      const { data } = payload;
      state.categories = data;
      utils.handleSuccess(state.status.getItemCategory, {
        message: 'message',
        status: 200,
      });

      return state;
    },
    [getItemCategory.rejected]: (state, { payload }) =>
      utils.handleError(state.status.getItemCategory, payload?.data),
    [getItemCategory.pending]: (state) => utils.handleLoading(state.status.getItemCategory),

    /*
		Bulk Delete
        */
    [bulkDelete.fulfilled]: (state, { payload }) => {
      const { data, message } = payload;

      utils.handleSuccess(state.status.bulkDelete, {
        message,
        status: 200,
      });

      return state;
    },
    [bulkDelete.rejected]: (state, { payload }) =>
      utils.handleError(state.status.bulkDelete, payload?.data),
    [bulkDelete.pending]: (state) => utils.handleLoading(state.status.bulkDelete),

    /*
		Bulk Hide
        */
    [bulkHide.fulfilled]: (state, { payload }) => {
      const { data, message } = payload;
      utils.handleSuccess(state.status.bulkHide, {
        message,
        status: 200,
      });

      return state;
    },
    [bulkHide.rejected]: (state, { payload }) =>
      utils.handleError(state.status.bulkHide, payload?.data),
    [bulkHide.pending]: (state) => utils.handleLoading(state.status.bulkHide),
  },
});

export const {
  clearState,
  clearItems,
  updateFilter,
  addAddon,
  removeAddon,
  updateAddon,
  updateAddonChoice,
  removeAddonChoice,
  setView,
} = slice.actions;
// module.exports.clearStateFavorite =  slice.actions.clearState
export default slice.reducer;
